import { useQuery } from "@tanstack/react-query";

import {
  ChallengeParticipantStatus,
  ChallengeTaskType,
  ChallengeVerification,
  getChallengeRecordingEventDates,
  type ChallengeTaskConfig,
} from "@fitness-app/data-models/entities/Challenge";
import { ExerciseRecordingStatus, type ExerciseRecording } from "@fitness-app/data-models/entities/ExerciseRecording";
import { type Trainee } from "@fitness-app/data-models/entities/Trainee";

import { supabase } from "~/store/initializeStore";

/**
 * TRE-1794 — every query here is scoped by `parentExerciseId` + a single `scheduledAt` day.
 *
 * The previous shape (`.in("parentExerciseId", …).in("traineeId", [every participant])`) does not
 * survive a real challenge: 459 participants × 12 dates is 5 510 rows, which PostgREST silently
 * truncates at 1 000, and the trainee filter alone pushed the request URL past 17 kB — the length
 * at which the `.in` filter starts failing outright. One day at a time keeps every page small and
 * the URL short, and the dates come for free from the task's own `config.eventDates`.
 *
 * The trade-off: a recording of the same exercise made by a NON-participant on one of the
 * challenge dates also shows up. Re-adding the participant filter is what made the request
 * unsendable, and the tab is a review surface (scoring itself does check participation), so the
 * stray row is accepted.
 */

export type ChallengeRecordingRow = ExerciseRecording & {
  exercise: { name: string } | null;
  trainee: Pick<Trainee, "id" | "firstName" | "lastName" | "email"> | null;
};

export type ChallengeRecordingTask = {
  id: string;
  title: string;
  points: number;
  verification: ChallengeVerification;
  parentExerciseId: string;
  eventDates: string[];
};

/** What the trainer wants to see. `submitted` is the default — the rest is placeholder noise. */
export const recordingStatusFilters = {
  toRate: [ExerciseRecordingStatus.RECORDED],
  submitted: [ExerciseRecordingStatus.RECORDED, ExerciseRecordingStatus.RATED],
  scheduled: [ExerciseRecordingStatus.SCHEDULED],
  all: [
    ExerciseRecordingStatus.SCHEDULED,
    ExerciseRecordingStatus.RECORDED,
    ExerciseRecordingStatus.RATED,
    ExerciseRecordingStatus.ARCHIVED,
    ExerciseRecordingStatus.PENDING,
  ],
} satisfies Record<string, ExerciseRecordingStatus[]>;

export type RecordingStatusFilter = keyof typeof recordingStatusFilters;

/**
 * Mirror of `ChallengeScoreCalculator.qualifyingRecordingDays()`: only `trainerApproval` waits for a
 * rating, every other verification mode scores the moment the upload lands. Keep the two in step —
 * the panel uses this to tell the trainer whether the points are already on the board.
 */
export const recordingScoresPoints = (
  status: ExerciseRecordingStatus | string,
  verification: ChallengeVerification,
): boolean =>
  verification === ChallengeVerification.TrainerApproval
    ? status === ExerciseRecordingStatus.RATED
    : status === ExerciseRecordingStatus.RECORDED || status === ExerciseRecordingStatus.RATED;

/** True when the upload alone awards the points and the trainer's rating is pure feedback. */
export const recordingPointsAreAutomatic = (verification: ChallengeVerification): boolean =>
  verification !== ChallengeVerification.TrainerApproval;

const RECORDING_SELECT = "*, exercise:parentExerciseId(name), trainee:traineeId!inner(id, firstName, lastName, email)";

/** Shared prefix so `useUpdateExerciseRecording`'s `invalidateQueries(["exercise_recordings"])` hits all of it. */
const recordingsKey = (challengeId: string) => ["exercise_recordings", "challenge", challengeId] as const;

type TaskRow = {
  id: string;
  title: string;
  points: number;
  verification: ChallengeVerification;
  parentExerciseId: string | null;
  config: ChallengeTaskConfig | null;
};

/** The challenge's `exerciseRecording` tasks — each one owns an exercise and its list of event dates. */
export const useChallengeRecordingTasks = (challengeId: string) =>
  useQuery({
    queryKey: [...recordingsKey(challengeId), "tasks"],
    enabled: Boolean(challengeId),
    queryFn: async (): Promise<ChallengeRecordingTask[]> => {
      const { data, error } = await supabase
        .from("challenge_task")
        .select("id, title, points, verification, parentExerciseId, config")
        .eq("challengeId", challengeId)
        .eq("type", ChallengeTaskType.ExerciseRecording)
        .returns<TaskRow[]>();

      if (error) {
        throw error;
      }

      return (data ?? [])
        .filter((task): task is TaskRow & { parentExerciseId: string } => Boolean(task.parentExerciseId))
        .map((task) => ({
          id: task.id,
          title: task.title,
          points: task.points,
          verification: task.verification,
          parentExerciseId: task.parentExerciseId,
          eventDates: [...getChallengeRecordingEventDates(task.config)].sort(),
        }));
    },
  });

/** Denominator for the per-date header ("nadesłane 5 / 459"). Leavers no longer owe a recording. */
export const useChallengeParticipantsCount = (challengeId: string) =>
  useQuery({
    queryKey: ["challenge-participants-count", challengeId],
    enabled: Boolean(challengeId),
    queryFn: async () => {
      const { count, error } = await supabase
        .from("challenge_participant")
        .select("id", { count: "exact", head: true })
        .eq("challengeId", challengeId)
        .neq("status", ChallengeParticipantStatus.Left);

      if (error) {
        throw error;
      }

      return count ?? 0;
    },
  });

export type RecordingDateSummary = { submitted: number; toRate: number };

/**
 * Per-date counts for the panel headers. One request per event date, each returning nothing but
 * the `status` of the submitted rows — bounded by the participant count, so always well under the
 * 1 000-row cap and exact.
 */
export const useChallengeRecordingsSummary = (challengeId: string, task: ChallengeRecordingTask | null) =>
  useQuery({
    queryKey: [...recordingsKey(challengeId), "summary", task?.id],
    enabled: Boolean(challengeId && task),
    queryFn: async (): Promise<Record<string, RecordingDateSummary>> => {
      if (!task) {
        return {};
      }

      const entries = await Promise.all(
        task.eventDates.map(async (date) => {
          const { data, count, error } = await supabase
            .from("exercise_recording")
            .select("status", { count: "exact" })
            .eq("parentExerciseId", task.parentExerciseId)
            .eq("scheduledAt", date)
            .in("status", recordingStatusFilters.submitted)
            .returns<{ status: ExerciseRecordingStatus }[]>();

          if (error) {
            throw error;
          }

          const toRate = (data ?? []).filter((row) => row.status === ExerciseRecordingStatus.RECORDED).length;

          return [date, { submitted: count ?? 0, toRate }] as const;
        }),
      );

      return Object.fromEntries(entries);
    },
  });

type PageParams = {
  challengeId: string;
  task: ChallengeRecordingTask;
  date: string;
  statusFilter: RecordingStatusFilter;
  search: string;
  page: number;
  pageSize: number;
  enabled: boolean;
};

/** One page of a single date, filtered server-side. `total` drives antd's pagination. */
export const useChallengeRecordingsPage = ({
  challengeId,
  task,
  date,
  statusFilter,
  search,
  page,
  pageSize,
  enabled,
}: PageParams) =>
  useQuery({
    queryKey: [...recordingsKey(challengeId), "page", task.id, date, statusFilter, search, page, pageSize],
    enabled,
    keepPreviousData: true,
    queryFn: async (): Promise<{ rows: ChallengeRecordingRow[]; total: number }> => {
      const from = (page - 1) * pageSize;
      const trimmed = search.trim();

      let query = supabase
        .from("exercise_recording")
        .select(RECORDING_SELECT, { count: "exact" })
        .eq("parentExerciseId", task.parentExerciseId)
        .eq("scheduledAt", date)
        .in("status", recordingStatusFilters[statusFilter]);

      if (trimmed) {
        const pattern = `%${trimmed}%`;
        // The `!inner` embed makes a filter on the trainee narrow the recordings themselves.
        query = query.or(`firstName.ilike.${pattern},lastName.ilike.${pattern},email.ilike.${pattern}`, {
          referencedTable: "trainee",
        });
      }

      const { data, count, error } = await query
        .order("updatedAt", { ascending: false })
        .range(from, from + pageSize - 1)
        .returns<ChallengeRecordingRow[]>();

      if (error) {
        throw error;
      }

      return { rows: data ?? [], total: count ?? 0 };
    },
  });
